import * as stylex from '@stylexjs/stylex'
import { type ElementType, type ReactNode } from 'react'
import { ButtonBase, type ButtonBaseProps } from '../ButtonBase/ButtonBase'
import { CircularProgress } from '../CircularProgress/CircularProgress'
import { colors } from '../tokens/colors.stylex'
import { typography } from '../tokens/typography.stylex'
import { Tooltip } from '../Tooltip/Tooltip'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'
import { ButtonIcon } from './ButtonIcon'

const sizeVariants = stylex.create({
  small: (hasBorder: boolean) => ({
    padding: hasBorder ? '5px 11px' : '6px 12px',
  }),
  medium: (hasBorder: boolean) => ({
    padding: hasBorder ? '7px 11px' : '8px 12px',
  }),
  large: (hasBorder: boolean) => ({
    padding: hasBorder ? '9px 11px' : '10px 12px',
  }),
  extraLarge: (hasBorder: boolean) => ({
    padding: hasBorder ? '11px 11px' : '12px 12px',
  }),
})

const loadingVariants = stylex.create({
  filled: {
    backgroundColor: colors.grey300,
  },
  outlined: {
    borderColor: colors.grey300,
    color: colors.grey300,
  },
  ghost: {
    color: colors.grey300,
  },
  text: {
    color: colors.grey300,
  },
  circle: {
    color: colors.grey300,
  },
})

const buttonVariants = stylex.create({
  filled: (color: Color) => ({
    backgroundColor: {
      'default': colors[`${color}600`],
      ':disabled': colors.grey300,
      '@media (hover: hover)': {
        'default': null,
        ':hover:not(:disabled)': colors[`${color}700`],
      },
    },
    color: colors.white,
  }),
  outlined: (color: Color) => ({
    borderColor: {
      'default': colors[`${color}600`],
      ':disabled': colors.grey300,
    },
    borderStyle: 'solid',
    borderWidth: 1,
    backgroundColor: {
      'default': 'transparent',
      ':disabled': 'transparent',
      '@media (hover: hover)': {
        'default': null,
        ':hover:not(:disabled)': colors[`${color}100`],
      },
    },
    color: {
      'default': colors[`${color}600`],
      ':disabled': colors.grey300,
    },
  }),
  text: (color: Color) => ({
    padding: 0,
    color: {
      'default': colors[`${color}600`],
      ':disabled': colors.grey300,
      '@media (hover: hover)': {
        'default': null,
        ':hover:not(:disabled)': colors[`${color}700`],
      },
    },
    minWidth: 'auto',
  }),
  ghost: (color: Color) => ({
    color: {
      'default': colors[`${color}600`],
      ':disabled': colors.grey300,
      '@media (hover: hover)': {
        'default': null,
        ':hover:not(:disabled)': colors[`${color}700`],
      },
    },
  }),
  circle: (color: Color) => ({
    padding: '12px 12px',
    borderRadius: '50%',
    backgroundColor: {
      'default': 'transparent',
      ':disabled': 'transparent',
      '@media (hover: hover)': {
        'default': null,
        ':hover:not(:disabled)': colors[`${color}100`],
      },
    },
    color: {
      'default': colors[`${color}600`],
      ':disabled': colors.grey300,
    },
  }),
})

const _baseStyles = stylex.create({
  root: {
    borderRadius: 4,
    transition: 'all 0.2s ease-in-out',
    fontSize: typography.sizeBody,
    fontWeight: 400,
    lineHeight: 1.4287,
    minWidth: 64,
  },
  startIcon: {
    marginRight: 4,
  },
  endIcon: {
    marginLeft: 4,
  },
  fullWidth: {
    width: '100%',
  },
})

export type Variant = 'filled' | 'outlined' | 'text' | 'ghost' | 'circle'
export type Color = 'primary' | 'secondary' | 'tertiary' | 'grey' | 'blue' | 'red'
export type Size = 'small' | 'medium' | 'large' | 'extraLarge'
export type Target = '_self' | '_blank' | '_parent' | '_top'

export type ButtonProps<RootComponent extends ElementType = 'button', BoxElement extends HTMLElement = HTMLButtonElement> = ButtonBaseProps<RootComponent, BoxElement> & {
  /**
   * 按鈕樣式變體
   * @default 'filled'
   */
  variant?: Variant
  /**
   * 按鈕顏色，使用主題色彩 token
   * @default 'primary'
   */
  color?: Color
  /**
   * 是否顯示載入狀態，載入中會停用互動
   */
  loading?: boolean
  /**
   * 載入指示器的位置
   * @default 'center'
   */
  loadingPosition?: 'start' | 'center' | 'end'
  /**
   * 按鈕左側的圖示
   */
  startIcon?: ReactNode
  /**
   * 按鈕右側的圖示
   */
  endIcon?: ReactNode
  /**
   * 是否撐滿父容器寬度
   */
  fullWidth?: boolean
  /**
   * 按鈕尺寸
   * @default 'medium'
   */
  size?: Size
  /**
   * 自訂根元素樣式（StyleX）
   */
  rootStyle?: stylex.StyleXStyles
  /**
   * 覆寫內部子元素樣式，可用的 key：root、startIcon、endIcon
   */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  /**
   * Disabled 時 hover 顯示的提示原因，僅在 `disabled` 為 true 且本 prop 有值時生效。
   */
  disabledReason?: ReactNode
}

/**
 * Button 用於觸發使用者操作（例如送出表單、開啟 dialog）。
 * 若需要導航至其他頁面，請使用 Link 元件。
 * 支援 filled / outlined / ghost / text / circle 五種變體，
 * 以及 loading 狀態與 start/end icon。
 *
 * @example
 * ```tsx
 * <Button variant="filled" color="primary">送出</Button>
 * <Button variant="outlined" startIcon={<CartOutlined />}>加入購物車</Button>
 * <Button loading loadingPosition="start" startIcon={<Save />}>儲存中</Button>
 * ```
 */
export const Button = <RootComponent extends ElementType = 'button', BoxElement extends HTMLElement = HTMLButtonElement>(props: ButtonProps<RootComponent, BoxElement>) => {
  const {
    component = 'button',
    variant = 'filled',
    color = 'primary',
    loading,
    fullWidth,
    loadingPosition = 'center',
    size = 'medium',
    startIcon,
    endIcon,
    rootStyle,
    xStyles,
    children,
    disableRipple: disableRippleProp,
    disabled,
    disabledReason,
    ...rest
  } = props
  const baseStyles = useXStyles(_baseStyles, xStyles)
  const disableRipple = disableRippleProp || variant === 'text'

  const renderIcon = (position: 'start' | 'end', icon?: ReactNode) => {
    if (!icon) return null
    return (
      <ButtonIcon rootStyle={baseStyles[`${position}Icon`]}>
        {loading && loadingPosition === position
          ? <CircularProgress />
          : icon}
      </ButtonIcon>
    )
  }

  const renderContent = () => {
    if (loading && loadingPosition === 'center') {
      return <ButtonIcon><CircularProgress /></ButtonIcon>
    }
    return children
  }

  const buttonElement = (
    <ButtonBase
      component={component}
      rootStyle={[
        baseStyles.root,
        loading && loadingVariants[variant],
        fullWidth && baseStyles.fullWidth,
        sizeVariants[size](variant === 'outlined'),
        buttonVariants[variant](color),
        rootStyle,
      ]}
      disableRipple={disableRipple}
      disabled={disabled || loading}
      {...rest}
    >
      {renderIcon('start', startIcon)}
      {renderContent()}
      {renderIcon('end', endIcon)}
    </ButtonBase>
  )

  if (disabled && disabledReason) {
    return (
      <Tooltip variant="dark" placement="top" PaperProps={{ maxWidth: 200 }} title={disabledReason} disablePortal={false} >
        {buttonElement}
      </Tooltip>
    )
  }

  return buttonElement
}
